'use client'

import { useEffect } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import { MainLayout } from './main-layout'
import { AuthGuard } from '../auth/auth-guard'
import { useAuth } from '@/lib/stores/auth-store'
import { usePermissions } from '@/lib/hooks/use-permissions'

interface ConditionalLayoutProps {
  children: React.ReactNode
}

/**
 * Pushes users with a pending temporary password to the change-password
 * screen before they can reach any authenticated page.
 */
function ForcePasswordChangeGate() {
  const pathname = usePathname()
  const router = useRouter()
  const { isAuthenticated } = useAuth()
  const { mustChangePassword, isLoading } = usePermissions()

  useEffect(() => {
    if (isAuthenticated && !isLoading && mustChangePassword && pathname !== '/change-password') {
      router.replace('/change-password')
    }
  }, [isAuthenticated, isLoading, mustChangePassword, pathname, router])

  return null
}

export function ConditionalLayout({ children }: ConditionalLayoutProps) {
  const pathname = usePathname()

  const publicRoutes = ['/login', '/signup', '/signup/verify', '/forgot-password', '/reset-password', '/join']
  const isPublicRoute = publicRoutes.includes(pathname)
  const isOnboarding = pathname === '/onboarding'
  const isChangePassword = pathname === '/change-password'

  if (isPublicRoute) {
    return <>{children}</>
  }

  // Onboarding and the forced password change run inside auth but without the
  // full app chrome.
  if (isOnboarding || isChangePassword) {
    return (
      <AuthGuard>
        {!isChangePassword && <ForcePasswordChangeGate />}
        {children}
      </AuthGuard>
    )
  }

  return (
    <AuthGuard>
      <ForcePasswordChangeGate />
      <MainLayout>
        {children}
      </MainLayout>
    </AuthGuard>
  )
}
